import ConditionalWrapper from '@/components/ui/custom/ConditionalWrapper';
import AvatarName from '@/components/ui/custom/dataDisplay/AvatarName';
import { useBankFilters } from '@/data/context/BankFiltersContext';
import useAuth from '@/data/hook/useAuth';
import useInfiniteService from '@/data/hook/useInfiniteService';
import { updateActivityInBank } from '@/data/services/_deprecated/activityServices';
import {
  lessonsQueryKeys,
  scheduledLessonsQueryKeys,
  usersQueryKeys,
} from '@/data/services/querykeys';
import { isSuperAdmin } from '@/functions/auth';
import Activity, { ActivityMeta } from '@/models/_deprecated/Activity';
import Lesson from '@/models/Lesson';
import { activityTypeOptions } from '@/utils/activityTypeOptions';
import { modalColors } from '@/utils/bank';
import {
  EyeIcon,
  EyeSlashIcon,
  PencilSquareIcon,
  ArrowDownOnSquareIcon,
} from '@heroicons/react/24/outline';
import { useMutation, useQuery } from '@tanstack/react-query';
import { compact } from 'lodash';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import Card from '../BankCards';
import ExpansibleQuestionCard from '../Expansible/ExpansibleQuestionCard';
import { useItems } from '../ListBankCards.hooks';
import { CreateCardProps } from './CreateCardProps';

export function CreateQuestionCard({
  item: activity,
  checkable,
  disabledAnimation,
  onUse,
  loading,
  selectedId,
  includedIds,
}: CreateCardProps<Activity, ActivityMeta>) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'modalBank',
  });
  const { t: tActivityTypes } = useTranslation('translation', {
    keyPrefix: 'activity.manageActivity.activityTypes',
  });
  const { user } = useAuth();

  const isSuper = isSuperAdmin(user?.userType);
  const { klassId, slugCourseName = '' } = useParams();

  const { filters } = useBankFilters();

  const { invalidate: questionQueryInvalidate } = useItems(
    'questions',
    filters,
    {
      enabled: false,
    },
  );

  const { results: scheduledLessons } = useInfiniteService({
    ...scheduledLessonsQueryKeys.list({
      klassId: Number(klassId),
      ordering: 'datetime',
    })._ctx.infinity,
    enabled: !isNaN(Number(klassId)),
  });

  const { results: lessons } = useInfiniteService({
    ...lessonsQueryKeys.list({ slug: slugCourseName })._ctx.infinity,
    enabled: !!slugCourseName && !klassId,
  });

  const lessonsUsage: Lesson[] = lessons.length
    ? lessons
    : compact(scheduledLessons.map(({ lesson }) => lesson));

  const activityInUse = lessonsUsage.some(lesson =>
    lesson.activities.some(
      scheduledActivityId => scheduledActivityId === activity.id,
    ),
  );

  const itemInUse = includedIds?.some(id => id === activity.id);

  const [inBank, setInBank] = useState(activity.inBank);

  const { data: author } = useQuery({
    ...usersQueryKeys.get(activity.author),
    refetchOnMount: false,
    enabled: isSuperAdmin(user?.userType),
  });

  const updateInBank = async (inBank: boolean) => {
    const bankUpdate = await updateActivityInBank({
      activityId: activity.id,
      bank: { inBank },
    });
    await questionQueryInvalidate();
    return bankUpdate;
  };

  const { mutate: onCheck, isLoading } = useMutation(updateInBank, {
    onSuccess: data => {
      setInBank(data.inBank);
    },
  });

  let tags: string[] = [activity.originCourse];
  tags = activityInUse || itemInUse ? tags.concat(t('inUseActivity')) : tags;

  tags = tags.concat(activity.tools.map(tool => tool.name));

  const mountDescIcons = (activity: Activity) => {
    const activityTypes = activity.questions.map(question => {
      const activityIcon = activityTypeOptions.find(
        item => item.type === question.type,
      );
      return activityIcon;
    });

    const activityTypesNotNullable = compact(activityTypes);

    const iconsElement = activityTypesNotNullable.map(
      ({ type, icon: Icon }, index) => ({
        icon: Icon && (
          <Card.IconContainer key={'questionIcon' + index}>
            <Icon className="w-5 text-primary" />
          </Card.IconContainer>
        ),
        tooltip: tActivityTypes(type),
      }),
    );
    return iconsElement;
  };

  let metaCards = [
    <Card.Meta
      key={'questionUse' + activity.id}
      icon={ArrowDownOnSquareIcon}
      label={activity.lessonsCount}
    />,
  ];

  const publicCard = (
    <Card.Meta
      key={'public' + activity.id}
      icon={activity.isPublicInBank ? EyeIcon : EyeSlashIcon}
      className="ml-2"
      tooltip={activity.isPublicInBank ? t('public') : t('private')}
    />
  );

  metaCards = isSuper ? [...metaCards, publicCard] : metaCards;

  return (
    <Card.Animation disabled={disabledAnimation}>
      <ConditionalWrapper
        condition={!!checkable}
        wrapper={
          <Card.Checkable
            checked={inBank}
            onCheck={onCheck}
            isLoading={isLoading}
          />
        }
      >
        <Card
          color={modalColors.questions}
          expansibleContent={
            <ExpansibleQuestionCard
              item={activity}
              onUse={onUse}
              used={activityInUse || itemInUse}
              loading={loading}
              selectedId={selectedId}
            />
          }
        >
          <Card.LeftContent>
            <Card.Header
              icon={PencilSquareIcon}
              title={activity.name}
              color={modalColors.questions.color}
              meta={metaCards}
            />
            <Card.DescContainer>
              <Card.Tags tags={tags} color={modalColors.questions} />
              {author && <AvatarName user={author} className="gap-2" />}
            </Card.DescContainer>
          </Card.LeftContent>
          <Card.IconsList icons={mountDescIcons(activity)} />
        </Card>
      </ConditionalWrapper>
    </Card.Animation>
  );
}
